import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ApiClient from '../utils/apiClient';
import type { ApiError } from '../utils/apiClient';
import styles from './ReleaseScheduleDialog.module.css';

/**
 * Bestätigungsdialog fürs Freigeben eines Stundenplans an Studierende (UX-Plan 3.5).
 *
 * Freigeben ist – anders als Speichern – eine Entscheidung mit Folgen für andere; deshalb
 * nennt der Dialog, welcher Plan sichtbar wird und welcher dafür unsichtbar wird. Der
 * Server setzt über `set-primary` genau einen Plan pro Kurs; `unset-primary` hebt auf.
 */
interface ReleaseScheduleDialogProps {
  classId: string;
  scheduleId: string;
  scheduleName: string;
  /** true = Freigabe aufheben statt erteilen */
  release: boolean;
  onClose: () => void;
  onDone: (released: boolean) => void;
}

interface ScheduleRow { id: string; name?: string | null; is_primary?: boolean | null }

export default function ReleaseScheduleDialog({ classId, scheduleId, scheduleName, release, onClose, onDone }: ReleaseScheduleDialogProps) {
  const [currentReleased, setCurrentReleased] = useState<ScheduleRow | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await ApiClient.get(`classes/${classId}/schedules`);
        const data = await response.json();
        if (cancelled) return;
        const rows: ScheduleRow[] = data.schedules || [];
        setCurrentReleased(rows.find(r => r.is_primary && r.id !== scheduleId) ?? null);
      } catch {
        if (!cancelled) setCurrentReleased(null);
      }
    })();
    return () => { cancelled = true; };
  }, [classId, scheduleId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const endpoint = release
        ? `classes/${classId}/schedules/${scheduleId}/set-primary`
        : `classes/${classId}/schedules/${scheduleId}/unset-primary`;
      await ApiClient.put(endpoint, undefined);
      toast.success(release ? `„${scheduleName}“ ist für Studierende freigegeben.` : `Freigabe von „${scheduleName}“ aufgehoben.`);
      onDone(release);
    } catch (caught) {
      const err = caught as ApiError;
      toast.error(err.originalErrorData?.detail || err.message || 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  const title = release ? 'An Studierende freigeben?' : 'Freigabe aufheben?';
  const displayName = scheduleName || 'Stundenplan';

  return (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="release-dialog-title" onClick={e => e.stopPropagation()}>
        <h2 id="release-dialog-title" className={styles.title}>{title}</h2>
        {release ? (
          <div className={styles.body}>
            <p>Studierende sehen ab jetzt ihren Termin aus <strong>„{displayName}“</strong>.</p>
            {currentReleased === undefined && <p className={styles.muted}>Prüfe, welcher Plan bisher freigegeben ist …</p>}
            {currentReleased && (
              <p>Der bisher freigegebene Plan <strong>„{currentReleased.name || 'Stundenplan'}“</strong> wird für sie unsichtbar.</p>
            )}
            <p className={styles.muted}>Eine Benachrichtigung verschickst du getrennt über „An Studierende senden“.</p>
          </div>
        ) : (
          <div className={styles.body}>
            <p>Studierende sehen danach <strong>keinen Termin mehr</strong> für diesen Kurs, bis du einen Plan freigibst.</p>
          </div>
        )}
        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onClose} disabled={busy}>Abbrechen</button>
          <button type="button" className={release ? styles.confirm : styles.confirmDanger} onClick={confirm} disabled={busy || (release && currentReleased === undefined)}>
            {busy ? 'Bitte warten …' : release ? 'Freigeben' : 'Freigabe aufheben'}
          </button>
        </div>
      </div>
    </div>
  );
}
